/**
 * editorBridge.ts — the blog editor's live handles, for the WebMCP seam.
 *
 * The WebMCP runtime executes on a Chrome tool call, outside the React tree, so
 * it cannot reach into `UniversalEditorPage` for the TipTap editor or the save
 * and publish handlers. This is the one narrow channel across that boundary: the
 * editor page publishes a small bundle of imperative handles on mount, and
 * `editorSeams.ts` reads it at call time.
 *
 * It is deliberately NOT a store of document content and NOT a generic command
 * bus. It carries the live `Editor`, the content id, and the two async writers
 * the page already owns (`saveNow`, `buildPublishHtml`). Everything an editor
 * command does is expressed through these, so the seam has no path to anything
 * the page did not hand it.
 *
 * Ownership is an opaque token per mounted page, exactly as in `focus.ts`, so a
 * route transition that mounts the next page before unmounting the previous one
 * cannot have the old page's cleanup blank the new page's slot.
 */
import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';

export interface EditorBridge {
  /** The `blog_content` row id the editor is on. */
  readonly contentId: string;
  /** The live TipTap editor. Null before it mounts or after it tears down. */
  getEditor(): Editor | null;
  /** Persist the current buffer to the draft row, exactly as the Save button. */
  saveNow(): Promise<void>;
  /**
   * The publish HTML, generated from the UNFILTERED content_json so CTA
   * `rawHtml` nodes survive — the same builder the Publish button uses. Resolves
   * undefined when there is nothing to publish.
   */
  buildPublishHtml(): Promise<string | undefined>;
}

interface BridgeSlot {
  readonly owner: object;
  readonly bridge: EditorBridge;
}

let slot: BridgeSlot | null = null;

/** Takes the slot for `owner`. A republish with the same owner replaces it. */
export function publishEditorBridge(owner: object, bridge: EditorBridge): void {
  slot = { owner, bridge };
}

/** Releases the slot only if `owner` still holds it. Same token compare as focus. */
export function releaseEditorBridge(owner: object): void {
  if (!slot || slot.owner !== owner) return;
  slot = null;
}

/**
 * Imperative read for the seam, which runs on a Chrome tool call and is not in
 * the React tree. Null means no editor page is mounted, which every editor
 * command treats as a refusal rather than acting on a document off screen.
 */
export function readEditorBridge(): EditorBridge | null {
  return slot?.bridge ?? null;
}

/**
 * Publishes the editor's handles for as long as the page is mounted.
 *
 * Safe under StrictMode's double mount and a no-op when nothing changed: the
 * bundle is rebuilt each render but the slot only holds the latest, and the
 * getters close over refs so a stale bundle still reads live values.
 */
export function useEditorBridge(bridge: EditorBridge | null): void {
  const ownerRef = useRef<object | null>(null);
  if (ownerRef.current === null) ownerRef.current = { surface: 'editor-bridge' };
  const owner = ownerRef.current;

  useEffect(() => {
    if (!bridge) {
      releaseEditorBridge(owner);
      return;
    }
    publishEditorBridge(owner, bridge);
  }, [owner, bridge]);

  useEffect(() => () => releaseEditorBridge(owner), [owner]);
}
